import { useEffect, useState } from 'react';

import { notificationSuccess as notify } from 'app/core/toast';
import { AxiosError } from 'axios';
import { type ResponseValues } from 'axios-hooks';
import i18next from 'i18next';
import { useNavigate } from 'react-router-dom';

import { useGetApplication } from '../../../hooks';
import { useCatalogValueList } from '../../configuration/catalogs/hooks/useCatalogValueList';
import { type CatalogValue } from '../../configuration/catalogs/models/Catalog';
import { BaseUrlTaxpayer } from '../../taxes/Administration/http';
import { messages, messagesKey } from '../../translation';
import { useTaxpayerRequestStore } from '../stores/useTaxpayerRequestStore';

import {
  base,
  getNotes,
  saveTaxypayerRequest,
  updateTaxypayerRequest
} from './services/taxpayerRequest.service';
import { isEnabled } from './enabled';
import {
  type EnrollmentTaxPayerInfo,
  type SaveRequest,
  type Tax,
  type Taxpayers,
  type TemplateWeb,
  type UpdateRequest
} from './types';

export const useGetTaxPayerRequest = (id: string) => {
  const result = useGetApplication<any>({
    url: `${base}/` + id,
    params: undefined
  });
  return result;
};
export const useGetTaxPayerRequestByTaxPayer = (id: string) => {
  const result = useGetApplication<any>({
    url: `${base}/taxpayer/` + id,
    params: undefined
  });
  return result;
};
interface Value {
  id: string;
  name: string;
  filingDueDay: number;
}
export const useGetTaxPeriodicities = (
  taxId: string,
  onLoad = true
): ResponseValues<Array<Value>, any, any> => {
  const url = `/taxes/${taxId}/TaxPeriodicity/All`;
  const [{ data, loading, error }] = useGetApplication<Array<any>>(
    {
      url: url,
      params: undefined
    },
    onLoad
  );

  return {
    data: data?.map((x) => {
      return {
        id: x.id,
        name: x.name,
        filingDueDay: x.filingDueDay
      };
    }),
    error,
    loading
  };
};

export const useGetReportPeriodicities = (
  reportId: string,
  onLoad = true
): ResponseValues<Array<Value>, any, any> => {
  const url = `/reports/${reportId}/ReportPeriodicity/All`;
  const [{ data, loading, error }] = useGetApplication<Array<any>>(
    {
      url: url,
      params: undefined
    },
    onLoad
  );

  return {
    data: data?.map((x) => {
      return {
        id: x.id,
        name: x.periodicityType?.name,
        filingDueDay: x.filingDueDay
      };
    }),
    error,
    loading
  };
};

const buildOperationsQuery = (operations?: Array<string>) => {
  const params = new URLSearchParams();
  operations?.forEach((operation) => params.append('operations', operation));
  return params.toString();
};

export const useGetTaxByOperations = (operations: Array<string>, onLoad = true) => {
  const response = useGetApplication<Array<Tax>>(
    {
      url: `taxes/All`,
      params: { operations }
    },
    onLoad
  );

  return response;
};

export const useGetNotes: (
  section: string,
  language: string
) => {
  loading: boolean;
  data: TemplateWeb;
} = (section: string, language: string) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<TemplateWeb>({ name: '', html: '' });

  useEffect(() => {
    getNotes(section, language).then((notes: TemplateWeb) => {
      setLoading(false);
      setData(notes);
    });
  }, []);

  return { loading, data };
};

export const useGetReportByOperations = (operations: Array<string>, onLoad = true) => {
  const operationsQuery = buildOperationsQuery(operations);
  const response = useGetApplication<Array<Tax>>(
    {
      url: operationsQuery ? `reports/All?${operationsQuery}` : 'reports/All',
      params: undefined
    },
    onLoad
  );

  return response;
};

export const useGetFilesDefinitionTaxpayerRequest: () => ResponseValues<
  Array<CatalogValue>,
  any,
  any
> = () => {
  const { data, error, isLoading: loading } = useCatalogValueList('files-definition');
  const result = data?.filter((x) => isEnabled(x.additionalFields.enable)) || [];

  return { data: result, error: error ? AxiosError.from(error) : null, loading };
};

export const useTaxpayerRequestCapabilities = () => {
  const [{ data: reports, loading: loadingReports, error: reportsError }] =
    useGetReportByOperations([]);
  const {
    data: fileDefinitions,
    loading: loadingFiles,
    error: filesError
  } = useGetFilesDefinitionTaxpayerRequest();
  const {
    data: contactFileDefinitions,
    isLoading: loadingContactFiles,
    error: contactFilesError
  } = useCatalogValueList('contact-files-definition');

  return {
    loading: loadingReports || loadingFiles || loadingContactFiles,
    error: reportsError || filesError || contactFilesError,
    reportsEnabled: (reports?.length ?? 0) > 0,
    attachmentsEnabled:
      (fileDefinitions?.length ?? 0) > 0 ||
      (contactFileDefinitions?.some((definition) =>
        isEnabled(definition.additionalFields.enable)
      ) ??
        false)
  };
};

export const useGetTaxPayers: () => ResponseValues<Taxpayers, any, any> = () => {
  const [{ data, error, loading }] = useGetApplication<Taxpayers>({
    url: `${BaseUrlTaxpayer}`,
    params: undefined
  });
  return { data: data, error, loading };
};

export const useTaxpayerRequestActions = () => {
  const navigate = useNavigate();
  const reset = useTaxpayerRequestStore((s) => s.reset);

  const saveRequest = async (data: SaveRequest) => {
    try {
      await saveTaxypayerRequest({
        info: {
          enrollmentTaxPayerInfo: {
            ...data.data,
            preferredLanguage: i18next.language
          }
        } as EnrollmentTaxPayerInfo,
        captcha: data.captcha
      });

      reset();
      notify(messages(messagesKey.savedSucessfully));
      navigate('/public/taxpayer-request/success');
    } catch (error) {
      throw error;
    }
  };

  const updateRequest = async (data: UpdateRequest) => {
    try {
      await updateTaxypayerRequest(data.id, {
        enrollmentTaxPayerInfo: {
          ...data.data,
          preferredLanguage: i18next.language
        }
      } as EnrollmentTaxPayerInfo);

      reset();
      notify(messages(messagesKey.savedSucessfully));
      navigate(-1);
    } catch {
      return;
    }
  };

  return { saveRequest, updateRequest };
};
